import React from 'react';
import { FaInfoCircle } from 'react-icons/fa';
import MarkdownEditor from './MarkdownEditor';

/**
 * Step 6: Setup Instructions
 * - Markdown editor with preview
 * - Common sections: Installation, Configuration, Usage
 */
const MCPServerFormStep6 = ({ formData, updateFormData }) => {
  const handleInstructionsChange = (markdown) => {
    updateFormData({ setupInstructions: markdown });
  };

  const insertTemplate = () => {
    const template = `# Installation

\`\`\`bash
npm install your-package-name
# or
pip install your-package-name
\`\`\`

# Configuration

1. Set up your environment variables:
   \`\`\`bash
   export API_KEY=your_api_key_here
   \`\`\`

2. Configure the connection in your MCP client.

# Usage

Describe how to use your MCP server:

- Feature 1: Description
- Feature 2: Description
- Feature 3: Description

## Example

\`\`\`javascript
// Example code showing usage
\`\`\`

# Troubleshooting

Common issues and solutions:

- **Issue 1**: Solution
- **Issue 2**: Solution
`;
    updateFormData({ setupInstructions: template });
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold mb-4 text-gray-900 dark:text-spooky-ghost-white">
          Setup Instructions
        </h2>
        <p className="text-gray-600 dark:text-gray-400 mb-6">
          Provide clear instructions for users to install and configure your server.
        </p>
      </div>

      {/* Template Helper */}
      {!formData.setupInstructions && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
          <div className="flex items-start justify-between">
            <div>
              <h3 className="font-semibold text-blue-900 dark:text-blue-300 mb-1">
                Need help getting started?
              </h3>
              <p className="text-sm text-blue-800 dark:text-blue-400">
                Click the button to insert a setup instructions template.
              </p>
            </div>
            <button
              type="button"
              onClick={insertTemplate}
              className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition text-sm font-medium"
            >
              Insert Template
            </button>
          </div>
        </div>
      )}

      {/* Markdown Editor */}
      <div>
        <label className="block text-sm font-semibold mb-2 text-gray-700 dark:text-gray-300">
          Instructions (Markdown) <span className="text-red-500">*</span>
        </label>
        <MarkdownEditor
          value={formData.setupInstructions}
          onChange={handleInstructionsChange}
          placeholder="Write your setup instructions using Markdown..."
        />
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
          <FaInfoCircle className="inline mr-1" />
          Use Markdown formatting for headers, code blocks, lists, and links
        </p>
      </div>

      {/* Best Practices */}
      <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
        <h3 className="font-semibold text-green-900 dark:text-green-300 mb-2">
          Best Practices for Setup Instructions
        </h3>
        <ul className="text-sm text-green-800 dark:text-green-400 space-y-1">
          <li>✓ Start with installation instructions (npm, pip, etc.)</li>
          <li>✓ Include all required environment variables</li>
          <li>✓ Provide configuration examples</li>
          <li>✓ Add code snippets with syntax highlighting</li>
          <li>✓ Include troubleshooting tips for common issues</li>
          <li>✓ Link to external documentation if available</li>
        </ul>
      </div>
    </div>
  );
};

export default MCPServerFormStep6;
